import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  updateAllOrgLocations,
  clearUpdateAllOrgLocationsResult,
  resyncAllOrders,
  clearResyncAllOrdersResult,
  backfillAttendeesForOrganization,
  clearBackfillAttendeesResult,
  fetchAllOrganizations,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";

export default function CRMMaintenance() {
  const dispatch = useDispatch();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    updatingAllOrgLocations: loading,
    updateAllOrgLocationsResult: result,
    updateAllOrgLocationsError: error,
    resyncingAllOrders: resyncingOrders,
    resyncAllOrdersResult: resyncResult,
    resyncAllOrdersError: resyncError,
    backfillingAttendees,
    backfillAttendeesResult: backfillResult,
    backfillAttendeesError: backfillError,
    organizationsFetched,
    organizationsLoading,
    organizations,
  } = crmState;
  const [locationOverrides, setLocationOverrides] = useState("");
  const [overridesError, setOverridesError] = useState("");
  const [showOverrides, setShowOverrides] = useState(false);
  const [backfillOrgId, setBackfillOrgId] = useState("");

  useEffect(() => {
    // Fetch organizations if not already fetched
    if (!organizationsFetched && !organizationsLoading) {
      dispatch(fetchAllOrganizations());
    }
    // Clear results when component unmounts
    return () => {
      dispatch(clearUpdateAllOrgLocationsResult());
      dispatch(clearResyncAllOrdersResult());
      dispatch(clearBackfillAttendeesResult());
    };
  }, [dispatch, organizationsFetched, organizationsLoading]);

  const handleUpdateAllOrgLocations = async () => {
    dispatch(clearUpdateAllOrgLocationsResult());
    setOverridesError("");

    let parsedOverrides = {};
    if (locationOverrides.trim()) {
      try {
        parsedOverrides = JSON.parse(locationOverrides);
        if (
          typeof parsedOverrides !== "object" ||
          Array.isArray(parsedOverrides)
        ) {
          setOverridesError("Overrides must be a valid JSON object");
          return;
        }
      } catch (e) {
        setOverridesError("Invalid JSON format: " + e.message);
        return;
      }
    }

    await dispatch(
      updateAllOrgLocations({ locationOverrides: parsedOverrides }),
    );
  };

  const handleResyncAllOrders = async () => {
    dispatch(clearResyncAllOrdersResult());
    await dispatch(resyncAllOrders({}));
  };

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Maintenance
      </Typography>

      <Box marginTop={4}>
        <Paper sx={{ p: 3, marginBottom: 3 }}>
          <Typography variant="h5" gutterBottom>
            Update All Organization Locations
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={3}>
            Manually trigger a sync of Square locations for all organizations.
            This will update location data from Square for all active
            organizations with Square integrations.
          </Typography>

          <Box marginBottom={2}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowOverrides(!showOverrides)}
            >
              {showOverrides ? "Hide" : "Show"} Location Overrides
            </Button>
          </Box>

          {showOverrides && (
            <Box marginBottom={2}>
              <TextField
                data-testid="location-overrides-text-field"
                fullWidth
                multiline
                rows={4}
                label="Location Overrides (JSON)"
                placeholder='{"location-id-1": {"name": "Custom Name", "status": "ACTIVE"}}'
                value={locationOverrides}
                onChange={(e) => {
                  setLocationOverrides(e.target.value);
                  setOverridesError("");
                }}
                error={!!overridesError}
                helperText={
                  overridesError ||
                  "Optional: JSON object mapping location IDs to override values"
                }
                sx={{ marginBottom: 2 }}
                disabled={loading}
              />

              {overridesError && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                  {overridesError}
                </Alert>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              Location sync completed successfully
              {result.processedCount !== undefined && (
                <Box marginTop={1}>
                  <Typography variant="body2">
                    Processed: {result.processedCount} | Success:{" "}
                    {result.successCount} | Errors: {result.errorCount}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAllOrgLocations}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Updating Locations..." : "Update All Locations"}
          </Button>
        </Paper>
      </Box>

      <Box marginTop={4}>
        <Paper sx={{ p: 3, marginBottom: 3 }}>
          <Typography variant="h5" gutterBottom>
            Resync All Orders
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={3}>
            Delete all orders from the root orders collection and resync them
            from all organizations. This will rebuild the global orders index.
          </Typography>

          {resyncError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {resyncError}
            </Alert>
          )}

          {resyncResult && (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              Orders resync completed successfully
              {resyncResult.organizationsProcessed !== undefined && (
                <Box marginTop={1}>
                  <Typography variant="body2">
                    Organizations Processed:{" "}
                    {resyncResult.organizationsProcessed} | Orders Synced:{" "}
                    {resyncResult.ordersSynced} | Orders Deleted:{" "}
                    {resyncResult.ordersDeleted}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleResyncAllOrders}
            disabled={resyncingOrders}
            startIcon={resyncingOrders ? <CircularProgress size={20} /> : null}
          >
            {resyncingOrders ? "Resyncing Orders..." : "Resync All Orders"}
          </Button>
        </Paper>
      </Box>

      <Box marginTop={4}>
        <Paper sx={{ p: 3, marginBottom: 3 }}>
          <Typography variant="h5" gutterBottom>
            Backfill Attendees
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={3}>
            Build or rebuild attendee history for an organization from existing
            tickets. Use this after enabling the feature or to fix data. Only
            tickets with the prebuilt email field are included.
          </Typography>

          {backfillError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {backfillError}
            </Alert>
          )}

          {backfillResult && (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              Attendee backfill completed
              {backfillResult.attendeesCreated !== undefined && (
                <Box marginTop={1}>
                  <Typography variant="body2">
                    Attendees: {backfillResult.attendeesCreated} | Tickets
                    processed: {backfillResult.ticketsProcessed} | Events:{" "}
                    {backfillResult.eventsProcessed}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <TextField
              select
              label="Organization"
              value={backfillOrgId}
              onChange={(e) => {
                setBackfillOrgId(e.target.value);
                dispatch(clearBackfillAttendeesResult());
              }}
              disabled={backfillingAttendees}
              size="small"
              sx={{ minWidth: 220 }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
            >
              <option value="">Select organization</option>
              {(organizations || []).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name || org.id}
                </option>
              ))}
            </TextField>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                if (!backfillOrgId) return;
                dispatch(clearBackfillAttendeesResult());
                await dispatch(
                  backfillAttendeesForOrganization({
                    organizationId: backfillOrgId,
                  }),
                );
              }}
              disabled={backfillingAttendees || !backfillOrgId}
              startIcon={
                backfillingAttendees ? (
                  <CircularProgress size={20} />
                ) : null
              }
            >
              {backfillingAttendees
                ? "Backfilling..."
                : "Backfill Attendees"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
